import { createContext, useContext } from "react";
import { FileInputDimensions, MediaItem } from "@rapid/types/media";
export interface VideoControls {
    videoRef: React.RefObject<HTMLVideoElement | null>;
    mediaItem: MediaItem;
    isPlaying: boolean;
    onFramePreviewsReady: () => void;
    onPauseClicked: (currentTime: number) => void;
    onPlayClicked?: () => void;
    onTimelineClick: (targetTime: number) => void;
    onClockTick: (currentTime: number, mediaItemId: string) => void;
}

export interface FilePreviewState {
    mediaRef: React.RefObject<HTMLImageElement | HTMLVideoElement | null>;
    mediaElementReady: boolean;
    zoomLevel: number;
    zoomX: number;
    zoomY: number;
    hasMultipleFiles: boolean;
    videoControls: VideoControls | null;
    predictionsLoopTime: number | null;
    predictionsLoopProgress: number | undefined;
    loopOverlayWindow: {
        leftPercent: number;
        widthPercent: number;
        loopStart: number;
        loopEnd: number;
    } | null;
    initializedVideosRef: React.RefObject<string[]>;
}

export interface FilePreviewActions {
    handleGetFileDimensions: (dimensions: {
        natural: FileInputDimensions;
        rendered: FileInputDimensions;
    }) => void;
    handleMediaReady: () => void;
    onVideoPause: (currentTime: number) => void;
    setPredictionsLoopTime: (time: number | null) => void;
    setPredictionsLoopProgress: (progress: number | undefined) => void;
    setVideoControls: (props: VideoControls | null) => void;
}

export interface FilePreviewContextType extends FilePreviewState, FilePreviewActions {}

export const FilePreviewContext = createContext<FilePreviewContextType | undefined>(undefined);

export const TEST_LOOP_WINDOW = 0.5;
export const TEST_LOOP_HALF_WINDOW = TEST_LOOP_WINDOW / 2;

export const getPredictionsLoopStartEnd = (predictionsLoopTime: number, fileDuration: number) => {
    const halfWindow = TEST_LOOP_HALF_WINDOW;
    const loopStart = Math.max(0, predictionsLoopTime - halfWindow);
    const loopEnd = Math.min(fileDuration, predictionsLoopTime + halfWindow);
    return { loopStart, loopEnd };
};
export const useFilePreview = () => {
    const context = useContext(FilePreviewContext);
    if (!context) {
        throw new Error("useFilePreview must be used within a FilePreviewProvider");
    }
    return context;
};
